import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Badge, Card, ErrorBox } from "@/components/ui";

type AgentResponse = {
  status: string;
  agent: string;
  reply: string;
  model_chain: string[];
  primary_model: string;
  fallback_model: string;
  model_status: Record<string, any>;
  actions: Array<Record<string, any>>;
  tool_results: Array<Record<string, any>>;
  audit: Record<string, any>;
};

export default function LiveAgent() {
  const [message, setMessage] = useState("");
  const [patientId, setPatientId] = useState("");
  const [clinicDate, setClinicDate] = useState("");
  const [clinicName, setClinicName] = useState("Radiation Oncology clinic");
  const [action, setAction] = useState("");
  const [fieldsJson, setFieldsJson] = useState("");
  const [reason, setReason] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [history, setHistory] = useState<Array<{ role: "user" | "agent"; text: string; data?: AgentResponse }>>([]);
  const chat = useMutation({
    mutationFn: () => api.post<AgentResponse>("/live-agent/chat", {
      message,
      patient_id: patientId || undefined,
      clinic_date: clinicDate || undefined,
      clinic_name: clinicName,
      action: action || undefined,
      fields: fieldsJson.trim() ? JSON.parse(fieldsJson) : {},
      reason: reason || undefined,
      confirm_text: confirmText || undefined,
    }),
    onSuccess: (data) => {
      setHistory((h) => [...h, { role: "user", text: message }, { role: "agent", text: data.reply, data }]);
      setMessage("");
      setConfirmText("");
    },
  });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-50">Hermes RPIS Live Agent</h1>
        <div className="text-sm text-slate-400">Hermes with GPT-5.5, Grok 4.3, Codex, Paperclip, n8n, RPIS tools</div>
      </div>
      <Card>
        <div className="grid md:grid-cols-4 gap-3 mb-3">
          <div><label className="label">Patient id</label><input className="input" value={patientId} onChange={(e) => setPatientId(e.target.value)} /></div>
          <div><label className="label">Clinic date</label><input className="input" type="date" value={clinicDate} onChange={(e) => setClinicDate(e.target.value)} /></div>
          <div><label className="label">Clinic name</label><input className="input" value={clinicName} onChange={(e) => setClinicName(e.target.value)} /></div>
          <div>
            <label className="label">Advanced action</label>
            <select className="input" value={action} onChange={(e) => setAction(e.target.value)}>
              <option value="">Hermes decides</option>
              {["create_clinic_session","update_patient_fields","delete_patient","archive_patient","restore_patient","create_task","create_appointment","create_alert","prepare_note","clinical_chronology_event","gpt55_reasoning","grok_fallback","paperclip_issue","memory_update","n8n_workflow","codex_system_change","health_check"].map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-3 mb-3">
          <div className="md:col-span-2"><label className="label">Fields JSON</label><textarea className="input min-h-[80px] font-mono text-xs" value={fieldsJson} onChange={(e) => setFieldsJson(e.target.value)} placeholder='{"c_tnm":"cT2N1M0","metastatic_site_codes":"OSS","rt_dose":"30 Gy"}' /></div>
          <div className="space-y-2">
            <div><label className="label">Reason</label><input className="input" value={reason} onChange={(e) => setReason(e.target.value)} /></div>
            <div><label className="label">Confirmation</label><input className="input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="DELETE PATIENT" /></div>
          </div>
        </div>
        <textarea className="input min-h-[140px]" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Talk to Hermes: ask about a patient, clinic prep, staging, notes, Paperclip follow-up, n8n status, or an explicit Codex system change." />
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button className="btn-primary" disabled={!message.trim() || chat.isPending} onClick={() => chat.mutate()}>{chat.isPending ? "Working..." : "Send"}</button>
          <Badge tone="info">Hermes first</Badge>
          <Badge tone="muted">auditable</Badge>
        </div>
        {chat.error ? <div className="mt-3"><ErrorBox error={chat.error} /></div> : null}
      </Card>
      <div className="space-y-3">
        {history.map((item, idx) => (
          <Card key={idx} className={item.role === "agent" ? "border-cyan-900/60" : ""}>
            <div className="flex flex-wrap gap-2 mb-2">
              <Badge tone={item.role === "agent" ? "info" : "muted"}>{item.role}</Badge>
              {item.data ? <Badge tone="muted">{item.data.status}</Badge> : null}
              {item.data?.agent ? <Badge tone="info">{item.data.agent}</Badge> : null}
            </div>
            <div className="text-sm text-slate-100 whitespace-pre-wrap">{item.text}</div>
            {item.data?.model_chain?.length ? <ModelChain chain={item.data.model_chain} /> : null}
            {item.data?.actions?.length ? <ActionResults title="RPIS actions" actions={item.data.actions} /> : null}
            {item.data?.tool_results?.length ? <ActionResults title="Tool results" actions={item.data.tool_results} /> : null}
            {item.data?.model_status ? <StatusPanel status={item.data.model_status} /> : null}
          </Card>
        ))}
      </div>
    </div>
  );
}

function ModelChain({ chain }: { chain: string[] }) {
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {chain.map((item) => <Badge key={item} tone="muted">{item}</Badge>)}
    </div>
  );
}

function ActionResults({ title, actions }: { title: string; actions: Array<Record<string, any>> }) {
  return (
    <div className="mt-3 space-y-2 text-xs">
      <div className="text-slate-400">{title}</div>
      {actions.map((a, i) => (
        <div key={i} className="card-tight">
          <Badge tone="info">{String(a.type || "action")}</Badge>
          {a.patient_id ? <Link className="ml-2 text-accent hover:underline" to={`/patients/${a.patient_id}`}>Open patient</Link> : null}
          {a.session_id ? <Link className="ml-2 text-accent hover:underline" to="/clinic-prep">Open clinic prep</Link> : null}
          <pre className="mt-2 whitespace-pre-wrap text-slate-400">{JSON.stringify(a, null, 2)}</pre>
        </div>
      ))}
    </div>
  );
}

function StatusPanel({ status }: { status: Record<string, any> }) {
  const hermesStatus = status.hermes?.status || "unknown";
  return (
    <div className="mt-3 text-xs text-slate-500">
      <div>Hermes: {String(hermesStatus)}</div>
      <pre className="mt-2 whitespace-pre-wrap">{JSON.stringify(status, null, 2)}</pre>
    </div>
  );
}
